import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { mobileUsersApi } from '../../axios';
import {
  MobileUser,
  MobileUserCreateBody,
  MobileUserUpdateBody,
  ModificationLog,
} from '../../types';
import type { SortByNameOrder } from '../../utils/sortByName';
import type { RootState } from '../store';

interface MobileUsersState {
  mobileUsers: MobileUser[];
  modificationLogs: ModificationLog[];
  page: number; // for the table pagination
  totalMobileUsers: number;
  mobileUserSearchQuery?: string;
  /**
   * DS-3967. The name sort lives here rather than in component state because
   * the two list thunks take no arguments and read the store, and the search
   * bar is a sibling of the table, so state inside the table cannot reach it.
   * AC-8 is met by an explicit reset on mount, not by unmounting this slice.
   */
  sortOrder: SortByNameOrder;
  /**
   * DS-4025. What the rows currently on screen actually are: which of the two
   * lists they came from, and the order, page and search they were fetched with.
   *
   * Every one of those three is applied optimistically and then asked of the
   * server, so on a failed read the screen claims things nothing ever loaded.
   * DS-3967 solved that for the order by emptying the list, which took the
   * control off screen with it and made the claim vacuous. The rows now survive a
   * failed read, because a mutation refetches immediately and blanking threw away
   * the edit it had just made correctly, so the claims have to be made true
   * instead of vacuous.
   *
   * The tab is in here for a reason that has nothing to do with the order or the
   * page: neither list thunk clears the rows in `pending` and the table does not
   * remount between the two tabs, so that blanking was the only thing scoping
   * rows to the list that fetched them. Without it a failed read after a tab
   * switch leaves active users sitting under Deactivated, offering Activar on
   * users that are already active.
   */
  lastLoadedList?: {
    list: 'activated' | 'deactivated';
    sortOrder: SortByNameOrder;
    page: number;
    search: string;
  };
  /**
   * DS-4023. The id of the most recent list request. The three list thunks all
   * write `mobileUsers` or clear it, and none of them cancels its predecessor,
   * so without this the screen shows whichever response arrives last rather than
   * the one that was asked for last.
   *
   * One field for all three on purpose: switching between Activated, Deactivated
   * and History dispatches different thunks, and a per-thunk field would let a
   * slow response from the tab you just left overwrite the tab you are on.
   *
   * DS-4025. The three mutations that edit the list claim it too. Without that a
   * list request already in flight stayed the current one across a mutation, and
   * its answer, built before the mutation, put the rows back. The two that write
   * a user without touching the list stay out of it, see `savingUser`.
   *
   * Claiming is only half of it. Discarding the losing answer is correct only if
   * something else is going to paint, so the two callers that mutate without
   * refetching now refetch, the way `handleDeactivateUser` always has.
   */
  latestUsersRequestId?: string;
  totalLogs?: number;
  loading: boolean;
  /**
   * DS-4025. In flight for the two mutations that write a user without touching
   * the list, `updateMobileUser` and `createMobileUser`.
   *
   * Neither has a stake in the request slot, so claiming one would discard a list
   * answer for nothing, and being in the slot means their own result can be
   * discarded by whatever claims it next. Create is the sharper case: its
   * `success` drives the snackbar, the tracker event and the redirect, so a list
   * request owning the slot would swallow the confirmation of a user that was
   * created. Their own flag keeps both out of the slot without stranding the
   * shared `loading`, which only a slot claimant may clear.
   */
  savingUser?: boolean;
  /**
   * DS-4025. The same ownership rule as the request slot, for `savingUser`. The
   * edit-groups modal closes without awaiting, so two `updateMobileUser` calls
   * can overlap, and without this the first answer back turned the flag off while
   * the second was still travelling. That is the original `loading` bug in
   * miniature; the blast radius is only the progress bar and the viewMore
   * disable, but the shape is what this whole ticket is about.
   */
  latestSavingRequestId?: string;
  error?: string;
  success?: boolean;
}

const mobileUsersInitialState: MobileUsersState = {
  mobileUsers: [],
  modificationLogs: [],
  page: 0,
  totalMobileUsers: 0,
  mobileUserSearchQuery: '',
  sortOrder: 'unsorted',
  loading: false,
};

// Async Thunks for API Calls:
const getActivatedMobileUsers = createAsyncThunk(
  'getActivatedMobileUsers',
  async (
    _,
    { getState }, // This comes from the payloadCreator. See https://redux-toolkit.js.org/api/createAsyncThunk#payloadcreator
  ) => {
    const store = getState() as Record<string, MobileUsersState>;
    return await mobileUsersApi.getActivatedMobileUsers({
      search: store.mobileUsers.mobileUserSearchQuery,
      page: store.mobileUsers.page + 1, // We need to +1 because MUI pagination starts on 0 and backend pagination starts on 1.
      sortOrder: store.mobileUsers.sortOrder,
    });
  },
);

const getDeactivatedMobileUsers = createAsyncThunk(
  'getDeactivatedMobileUsers',
  async (
    _,
    { getState }, // This comes from the payloadCreator. See https://redux-toolkit.js.org/api/createAsyncThunk#payloadcreator
  ) => {
    const store = getState() as Record<string, MobileUsersState>;
    return await mobileUsersApi.getDeactivatedMobileUsers({
      page: store.mobileUsers.page + 1, // We need to +1 because MUI pagination starts on 0 and backend pagination starts on 1.
      // DS-4034. The same query field the activated list reads: the box belongs
      // to the tab that is showing, so only one of the two lists ever uses it.
      search: store.mobileUsers.mobileUserSearchQuery,
      sortOrder: store.mobileUsers.sortOrder,
    });
  },
);

const getMobileUsersHistory = createAsyncThunk(
  'getMobileUsersHistory',
  async (
    _,
    { getState }, // This comes from the payloadCreator. See https://redux-toolkit.js.org/api/createAsyncThunk#payloadcreator
  ) => {
    const store = getState() as Record<string, MobileUsersState>;
    return await mobileUsersApi.getMobileUsersHistory(
      { page: store.mobileUsers.page + 1 }, // We need to +1 because MUI pagination starts on 0 and backend pagination starts on 1.
    );
  },
);

const deleteMobileUser = createAsyncThunk(
  'deleteMobileUser',
  async ({ mobileUserId }: { mobileUserId: number }) => {
    return await mobileUsersApi.deleteMobileUser(mobileUserId);
  },
);

const bulkDeactivateMobileUsers = createAsyncThunk(
  'bulkDeactivateMobileUsers',
  async ({ mobileUserIds }: { mobileUserIds: number[] }) => {
    return await mobileUsersApi.bulkDeactivateMobileUsers(mobileUserIds);
  },
);

const reactivateMobileUser = createAsyncThunk(
  'reactivateMobileUser',
  async ({ mobileUserId }: { mobileUserId: number }) => {
    return await mobileUsersApi.reactivateMobileUser(mobileUserId);
  },
);

const createMobileUser = createAsyncThunk(
  'createMobileUser',
  async (body: MobileUserCreateBody) => {
    return await mobileUsersApi.createMobileUser(body);
  },
);

const updateMobileUser = createAsyncThunk(
  'updateMobileUser',
  async (body: MobileUserUpdateBody) => {
    return await mobileUsersApi.updateMobileUser(body);
  },
);

/**
 * Claims the slot: from here on, this is the request whose answer counts.
 * Any earlier one still in flight becomes stale by definition.
 */
function beginUsersRequest(state: MobileUsersState, requestId: string) {
  state.latestUsersRequestId = requestId;
  state.loading = true;
  state.error = undefined;
}

function beginUserSave(state: MobileUsersState, requestId: string) {
  state.latestSavingRequestId = requestId;
  state.savingUser = true;
}

/** Clears `savingUser` only for the write that still owns it. */
function settleUserSave(state: MobileUsersState, requestId: string) {
  if (state.latestSavingRequestId !== requestId) return;
  state.latestSavingRequestId = undefined;
  state.savingUser = false;
}

/**
 * Records what the rows that just landed actually are, so a later failure can put
 * the screen's claims back to something true rather than to a default.
 */
function rememberLoadedList(state: MobileUsersState, list: 'activated' | 'deactivated') {
  state.lastLoadedList = {
    list,
    sortOrder: state.sortOrder,
    page: state.page,
    search: state.mobileUserSearchQuery ?? '',
  };
}

/**
 * Puts the screen back to the rows it is actually showing after a failed read.
 *
 * Rows from the other list are cleared: they are not stale, they are wrong, and
 * nothing else scopes them to a tab. Rows from this one stay, and the order and
 * page go back to the ones they were fetched with instead of the ones the user
 * asked for and never got.
 */
function revertToLoadedList(state: MobileUsersState, list: 'activated' | 'deactivated') {
  const loaded = state.lastLoadedList?.list === list ? state.lastLoadedList : undefined;
  if (!loaded) {
    state.mobileUsers = [];
    state.totalMobileUsers = 0;
    state.lastLoadedList = undefined;
  }
  state.sortOrder = loaded?.sortOrder ?? 'unsorted';
  state.page = loaded?.page ?? 0;
  // The search is the fourth claim and the sharpest of them when it is a *clear*
  // that failed: the box empties, the rows and the count stay filtered, and the
  // paginator reads a total with no filter shown, which looks like data loss
  // rather than a failed request.
  state.mobileUserSearchQuery = loaded?.search ?? '';
}

/**
 * Steps back a page when a mutation leaves the current one empty, never below the
 * first. Deactivating every row on page 0 used to drive `page` to -1, which just
 * sat there until the next action; the refetch now reads it straight away and
 * sends `page: 0` against pagination that starts at 1.
 */
function stepBackIfPageEmptied(state: MobileUsersState, remaining: number) {
  if (remaining !== 0 || state.page === 0) return;
  state.page--;
  // Keep the record in step, or a failed refetch would restore the page this
  // mutation just emptied. No user-visible difference today, since the rows are
  // empty in exactly this branch and the empty state replaces the paginator, but
  // it becomes real the moment anything offers a retry from there.
  if (state.lastLoadedList) state.lastLoadedList.page = state.page;
}

/**
 * True when the answer belongs to a request that is no longer the current one,
 * which includes the case where nothing is current because the table unmounted.
 */
function isStaleUsersResponse(state: MobileUsersState, requestId: string) {
  return state.latestUsersRequestId !== requestId;
}

// Mobile Users Slice with reducers:
export const mobileUsersSlice = createSlice({
  name: 'mobileUsers',
  initialState: mobileUsersInitialState,
  reducers: {
    cleanMobileUsers: () => {
      return { ...mobileUsersInitialState };
    },
    updateMobileUsersPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    updateMobileUserSearchQuery: (state, action: PayloadAction<string>) => {
      state.mobileUserSearchQuery = action.payload;
    },
    // Only the order. Every caller that changes what the list contains already
    // dispatches its own updateMobileUsersPage(0), so folding it in here would
    // duplicate that rather than save it. An earlier version of this comment
    // claimed the tab switch of AC-9 needed to carry the order without touching
    // the page; that caller does not exist.
    updateMobileUsersSortOrder: (state, action: PayloadAction<SortByNameOrder>) => {
      state.sortOrder = action.payload;
    },
    // Dispatched when the table unmounts. Leaving no request current means any
    // answer still on its way is stale, so it cannot write into a screen that is
    // no longer showing it.
    discardInFlightUsersRequests: (state) => {
      state.latestUsersRequestId = undefined;
      state.loading = false;
      state.latestSavingRequestId = undefined;
      state.savingUser = false;
      // The error goes with it. Left behind, a failure from the previous visit
      // survives the unmount and re-fires the snackbar on the next mount, for a
      // request that screen never made.
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    // Get Activated users:
    builder.addCase(getActivatedMobileUsers.pending, (state, action) => {
      beginUsersRequest(state, action.meta.requestId);
    });
    builder.addCase(getActivatedMobileUsers.rejected, (state, action) => {
      if (isStaleUsersResponse(state, action.meta.requestId)) return;

      state.loading = false;
      revertToLoadedList(state, 'activated');
      state.error = 'Error at fetching activated mobile users';
    });
    builder.addCase(getActivatedMobileUsers.fulfilled, (state, action) => {
      if (isStaleUsersResponse(state, action.meta.requestId)) return;

      state.mobileUsers = action.payload.users;
      state.totalMobileUsers = action.payload.total_users;
      rememberLoadedList(state, 'activated');
      state.loading = false;
      state.error = undefined;
    });

    // Get Deactivated users:
    builder.addCase(getDeactivatedMobileUsers.pending, (state, action) => {
      beginUsersRequest(state, action.meta.requestId);
    });
    builder.addCase(getDeactivatedMobileUsers.rejected, (state, action) => {
      if (isStaleUsersResponse(state, action.meta.requestId)) return;

      state.loading = false;
      revertToLoadedList(state, 'deactivated');
      state.error = 'Error at fetching deactivated mobile users';
    });
    builder.addCase(getDeactivatedMobileUsers.fulfilled, (state, action) => {
      if (isStaleUsersResponse(state, action.meta.requestId)) return;

      state.mobileUsers = action.payload.users;
      state.totalMobileUsers = action.payload.total_users;
      rememberLoadedList(state, 'deactivated');
      state.loading = false;
      state.error = undefined;
    });

    // Get history of users:
    builder.addCase(getMobileUsersHistory.pending, (state, action) => {
      state.mobileUsers = []; // Clean mobile users, we will not use them in this tab
      beginUsersRequest(state, action.meta.requestId);
    });
    // Unlike the other two, this one does not reset sortOrder. The history tab
    // renders ModificationLogsTable, which carries no sort control, so there is
    // no claim on screen that an empty list could contradict.
    builder.addCase(getMobileUsersHistory.rejected, (state, action) => {
      if (isStaleUsersResponse(state, action.meta.requestId)) return;

      state.modificationLogs = [];
      state.totalMobileUsers = 0;
      state.totalLogs = 0;
      state.loading = false;
      state.error = 'Error at fetching mobile users history';
    });
    builder.addCase(getMobileUsersHistory.fulfilled, (state, action) => {
      if (isStaleUsersResponse(state, action.meta.requestId)) return;

      state.modificationLogs = action.payload.users;
      state.totalLogs = action.payload.total_users;
      state.loading = false;
      state.error = undefined;
    });

    // Delete (deactivate) a mobile user:
    builder.addCase(deleteMobileUser.pending, (state, action) => {
      beginUsersRequest(state, action.meta.requestId);
    });
    builder.addCase(deleteMobileUser.rejected, (state, action) => {
      if (isStaleUsersResponse(state, action.meta.requestId)) return;
      state.loading = false;
      state.error = 'Error at deactivating mobile users';
    });
    builder.addCase(deleteMobileUser.fulfilled, (state, action) => {
      if (isStaleUsersResponse(state, action.meta.requestId)) return;
      state.loading = false;
      state.error = undefined;
      const filteredMobileUsers = [...state.mobileUsers].filter(
        (user) => user.id !== action.payload.id,
      );
      state.totalMobileUsers--;
      state.mobileUsers = filteredMobileUsers;
      stepBackIfPageEmptied(state, filteredMobileUsers.length);
      state.success = true;
    });

    // Bulk deactivate mobile users:
    builder.addCase(bulkDeactivateMobileUsers.pending, (state, action) => {
      beginUsersRequest(state, action.meta.requestId);
    });
    builder.addCase(bulkDeactivateMobileUsers.rejected, (state, action) => {
      if (isStaleUsersResponse(state, action.meta.requestId)) return;
      state.loading = false;
      state.error = 'Error at deactivating mobile users';
    });
    builder.addCase(bulkDeactivateMobileUsers.fulfilled, (state, action) => {
      if (isStaleUsersResponse(state, action.meta.requestId)) return;
      state.loading = false;
      state.error = undefined;
      const filteredMobileUsers = [...state.mobileUsers].filter(
        (user) => !action.meta.arg.mobileUserIds.includes(user.id),
      );
      state.totalMobileUsers -= action.meta.arg.mobileUserIds.length;
      state.mobileUsers = filteredMobileUsers;
      stepBackIfPageEmptied(state, filteredMobileUsers.length);
      state.success = true;
    });

    // Reactivate a mobile user:
    builder.addCase(reactivateMobileUser.pending, (state, action) => {
      beginUsersRequest(state, action.meta.requestId);
    });
    builder.addCase(reactivateMobileUser.rejected, (state, action) => {
      if (isStaleUsersResponse(state, action.meta.requestId)) return;
      state.loading = false;
      // No `error` here. That field is the table's own channel, and its effect
      // renders a generic "there was an error" for anything that sets it. It was
      // the only voice this path had until the row started reporting its own
      // failure specifically; keeping both showed two snackbars for one failure,
      // one of them vaguer than the other. The row's message is the single one.
    });
    builder.addCase(reactivateMobileUser.fulfilled, (state, action) => {
      if (isStaleUsersResponse(state, action.meta.requestId)) return;
      // Activating a user means removing it from the deactivated table (which we are currently on)
      state.loading = false;
      state.error = undefined;
      const filteredMobileUsers = [...state.mobileUsers].filter(
        (user) => user.id !== action.payload.id,
      );
      state.totalMobileUsers--;
      state.mobileUsers = filteredMobileUsers;
      stepBackIfPageEmptied(state, filteredMobileUsers.length);
      state.success = true;
    });

    // Create a mobile user:
    builder.addCase(createMobileUser.pending, (state, action) => {
      beginUserSave(state, action.meta.requestId);
      state.error = undefined;
      state.success = undefined;
    });
    // `success` and `error` are written unconditionally, unlike the flag. They are
    // this creation's own result rather than a claim about what is in flight, and
    // the form submits one at a time, so there is no second creation for them to
    // be stale against. They drive the snackbar, the tracker event and the
    // redirect, which is exactly what must not be swallowed.
    builder.addCase(createMobileUser.rejected, (state, action) => {
      settleUserSave(state, action.meta.requestId);
      state.error = 'Error at activating mobile users';
      state.success = false;
    });
    builder.addCase(createMobileUser.fulfilled, (state, action) => {
      settleUserSave(state, action.meta.requestId);
      state.error = undefined;
      state.success = true;
    });

    // Update mobile user:
    builder.addCase(updateMobileUser.pending, (state, action) => {
      beginUserSave(state, action.meta.requestId);
    });
    builder.addCase(updateMobileUser.rejected, (state, action) => {
      settleUserSave(state, action.meta.requestId);
    });
    builder.addCase(updateMobileUser.fulfilled, (state, action) => {
      settleUserSave(state, action.meta.requestId);
    });
  },
});

export const {
  cleanMobileUsers,
  updateMobileUsersPage,
  updateMobileUserSearchQuery,
  updateMobileUsersSortOrder,
  discardInFlightUsersRequests,
} = mobileUsersSlice.actions;
export {
  getActivatedMobileUsers,
  getDeactivatedMobileUsers,
  getMobileUsersHistory,
  deleteMobileUser,
  bulkDeactivateMobileUsers,
  reactivateMobileUser,
  createMobileUser,
  updateMobileUser,
};
export const selectMobileUsers = (state: RootState) => state.mobileUsers;
export default mobileUsersSlice.reducer;
